import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Lock, CheckCircle } from 'lucide-react';

const LockedGuard = ({ children }) => {
  const { user } = useAuth();
  if (user?.status !== 'active') return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
      <Lock size={48} color="#f59e0b" style={{ margin: '0 auto 16px' }} />
      <h2 style={{ color: '#f59e0b' }}>Account Not Activated</h2>
      <p style={{ color: '#64748b', marginBottom: 20 }}>Activate your account to access this feature. Pay KES 100.</p>
      <a href="/dashboard" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block' }}>Activate Account</a>
    </div>
  );
  return children;
};

// ── TRIVIA PAGE ────────────────────────────────────────────────
export const TriviaPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/earn/trivia/today')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load trivia'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const choose = (questionId, index) => setSelected(prev => ({ ...prev, [questionId]: index }));

  const submit = async () => {
    if (!data?.questions) return;
    if (Object.keys(selected).length < data.questions.length) {
      toast.error('Answer all 5 questions first');
      return;
    }
    setSubmitting(true);
    try {
      const answers = data.questions.map(q => ({ question_id: q.id, selected_index: selected[q.id] }));
      const res = await api.post('/earn/trivia/submit', { answers });
      setResult(res.data);
      toast.success(`Earned KES ${res.data.earning_amount}!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading trivia...</div>;

  return (
    <LockedGuard>
      <div className="page">
        <h1 className="page-title">Daily Trivia</h1>
        <p className="page-desc">Answer 5 questions to earn KES {data?.reward ?? 20} today.</p>

        {(data?.completed || result) ? (
          <div className="empty">
            <CheckCircle size={40} color="#22c55e" style={{ marginBottom: 12 }} />
            <p>You've completed today's trivia — come back tomorrow for more!</p>
            <p style={{ marginTop: 8, color: 'var(--green)', fontWeight: 700 }}>
              Earned KES {result?.earning_amount ?? data?.earning_amount}
            </p>
          </div>
        ) : (
          <div className="form-card" style={{ maxWidth: '100%' }}>
            {data?.questions?.map((q, i) => (
              <div key={q.id} className="form-group">
                <label>{i + 1}. {q.question}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {q.options.map((opt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => choose(q.id, idx)}
                      className="btn-primary"
                      style={{
                        background: selected[q.id] === idx ? 'var(--primary)' : 'var(--surface2)',
                        color: selected[q.id] === idx ? '#fff' : 'var(--text)',
                        textAlign: 'left', fontWeight: 500,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button className="btn-primary" disabled={submitting} onClick={submit} style={{ marginTop: 8 }}>
              {submitting ? 'Submitting...' : 'Submit Answers'}
            </button>
          </div>
        )}
      </div>
    </LockedGuard>
  );
};

// ── WATCH & EARN PAGE ────────────────────────────────────────────
export const WatchEarnPage = () => {
  const [videos, setVideos] = useState([]);
  const [rewardPerVideo, setRewardPerVideo] = useState(30);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // video currently playing
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef(null);

  const load = () => {
    api.get('/earn/videos')
      .then(r => { setVideos(r.data.videos); setRewardPerVideo(r.data.reward_per_video); })
      .catch(() => toast.error('Failed to load videos'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startVideo = async (video) => {
    try {
      await api.post(`/earn/videos/${video.id}/start`);
      setActive(video);
      setRemaining(video.duration_seconds);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start video');
    }
  };

  const claim = async () => {
    if (!active) return;
    try {
      const res = await api.post(`/earn/videos/${active.id}/complete`);
      toast.success(`Earned KES ${res.data.earning_amount}!`);
      setActive(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Not watched long enough yet');
    }
  };

  if (loading) return <div className="loading">Loading videos...</div>;

  return (
    <LockedGuard>
      <div className="page">
        <h1 className="page-title">Watch & Earn</h1>
        <p className="page-desc">Watch each ad video fully to earn KES {rewardPerVideo} per video, once per video per day.</p>

        {active && (
          <div className="form-card" style={{ maxWidth: '100%', textAlign: 'center' }}>
            <h3 style={{ marginBottom: 12 }}>{active.title}</h3>
            <video
              key={active.id}
              src={active.video_url}
              controls
              autoPlay
              style={{ width: '100%', maxWidth: 480, borderRadius: 10, marginBottom: 16 }}
            />
            <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
              {remaining > 0 ? `Keep watching — ${remaining}s left` : 'Video complete — claim your reward!'}
            </p>
            <button className="btn-primary" onClick={claim} disabled={remaining > 0}>
              {remaining > 0 ? `Wait ${remaining}s` : `Claim KES ${rewardPerVideo}`}
            </button>
          </div>
        )}

        {videos.length === 0
          ? <div className="empty">No videos available right now. Check back soon!</div>
          : <div className="tasks-grid">
              {videos.map(v => (
                <div key={v.id} className="task-card">
                  <div className="task-body">
                    <h3>{v.title}</h3>
                    <p>{v.duration_seconds}s ad video</p>
                    {v.my_status === 'completed' ? (
                      <span className="submission-badge approved">Earned today</span>
                    ) : (
                      <button className="task-cta-btn" onClick={() => startVideo(v)} style={{ border: 'none', cursor: 'pointer' }}>
                        {v.my_status === 'started' ? 'Resume Video' : 'Watch Video'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    </LockedGuard>
  );
};

// ── SURVEYS PAGE ───────────────────────────────────────────────
export const SurveysPage = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // survey being answered
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get('/earn/surveys')
      .then(r => setSurveys(r.data.surveys))
      .catch(() => toast.error('Failed to load surveys'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openSurvey = (survey) => { setOpen(survey); setAnswers({}); };

  const submit = async () => {
    if (!open) return;
    const answerList = open.questions.map((q, i) => answers[i] || '');
    if (answerList.some(a => !String(a).trim())) {
      toast.error('Please answer every question');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/earn/surveys/${open.id}/submit`, { answers: answerList });
      toast.success(`Earned KES ${res.data.earning_amount}!`);
      setOpen(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading surveys...</div>;

  if (open) {
    return (
      <LockedGuard>
        <div className="page">
          <h1 className="page-title">{open.title}</h1>
          {open.description && <p className="page-desc">{open.description}</p>}
          <div className="form-card" style={{ maxWidth: '100%' }}>
            {open.questions.map((q, i) => (
              <div key={i} className="form-group">
                <label>{i + 1}. {q.question || q}</label>
                <textarea
                  value={answers[i] || ''}
                  onChange={e => setAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                  placeholder="Your answer"
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                {submitting ? 'Submitting...' : `Submit — Earn KES ${open.reward_amount}`}
              </button>
              <button className="btn-primary" style={{ background: 'var(--surface2)', color: 'var(--text)' }} onClick={() => setOpen(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </LockedGuard>
    );
  }

  return (
    <LockedGuard>
      <div className="page">
        <h1 className="page-title">Surveys</h1>
        <p className="page-desc">Share your opinion and earn per survey.</p>
        {surveys.length === 0
          ? <div className="empty">No surveys available right now. Check back soon!</div>
          : <div className="tasks-grid">
              {surveys.map(s => (
                <div key={s.id} className="task-card">
                  <div className="task-body">
                    <h3>{s.title}</h3>
                    <p>{s.description || `${s.questions.length} questions`}</p>
                    {s.my_earning != null ? (
                      <span className="submission-badge approved">Earned KES {s.my_earning}</span>
                    ) : (
                      <button className="task-cta-btn" onClick={() => openSurvey(s)} style={{ border: 'none', cursor: 'pointer' }}>
                        Start — Earn KES {s.reward_amount}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    </LockedGuard>
  );
};

// ── BLOGGING PAGE ──────────────────────────────────────────────
export const BlogPage = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get('/earn/blogs')
      .then(r => setTopics(r.data.topics))
      .catch(() => toast.error('Failed to load blog topics'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (!open) return;
    if (wordCount < (open.min_words || 150)) {
      toast.error(`Write at least ${open.min_words || 150} words (currently ${wordCount})`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/earn/blogs/${open.id}/submit`, { content });
      toast.success(res.data.message);
      setOpen(null); setContent('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit post');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading blog topics...</div>;

  if (open) {
    return (
      <LockedGuard>
        <div className="page">
          <h1 className="page-title">{open.title}</h1>
          {open.brief && <p className="page-desc">{open.brief}</p>}
          <div className="form-card" style={{ maxWidth: '100%' }}>
            <div className="form-group">
              <label>Your post ({wordCount} / {open.min_words || 150} words minimum)</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Write your post here..."
                style={{ minHeight: 220 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                {submitting ? 'Submitting...' : `Submit — Earn KES ${open.reward_amount}`}
              </button>
              <button className="btn-primary" style={{ background: 'var(--surface2)', color: 'var(--text)' }} onClick={() => { setOpen(null); setContent(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </LockedGuard>
    );
  }

  return (
    <LockedGuard>
      <div className="page">
        <h1 className="page-title">Blogging</h1>
        <p className="page-desc">Write a post on one of these topics — approved posts are paid out.</p>
        {topics.length === 0
          ? <div className="empty">No blog topics available right now. Check back soon!</div>
          : <div className="tasks-grid">
              {topics.map(t => (
                <div key={t.id} className="task-card">
                  <div className="task-body">
                    <h3>{t.title}</h3>
                    <p>{t.brief || `Minimum ${t.min_words} words`}</p>
                    {t.my_submission ? (
                      <span className={`submission-badge ${t.my_submission.status}`}>
                        {t.my_submission.status === 'approved' ? `Approved - KES ${t.my_submission.earning_amount}` :
                         t.my_submission.status === 'pending' ? 'Pending Admin Review' : 'Rejected'}
                      </span>
                    ) : (
                      <button className="task-cta-btn" onClick={() => setOpen(t)} style={{ border: 'none', cursor: 'pointer' }}>
                        Write Post — Earn KES {t.reward_amount}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    </LockedGuard>
  );
};
